// src/components/AddPhone.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  linkWithCredential,
  signInWithCredential,
} from 'firebase/auth';
import { updateDoc } from 'firebase/firestore';

import PhoneInput from 'react-phone-number-input'; // NEW
import 'react-phone-number-input/style.css';       // place also in src/index if you like

import { auth } from '../firebase';
import { userDoc } from '../utils/firestoreRefs';

function AddPhone() {
  const navigate = useNavigate();

  // Single phone string from phone-number-input
  const [phoneValue, setPhoneValue] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [verificationCode,  setVerificationCode]    = useState('');
  const [loading,           setLoading]             = useState(false);
  const [error,             setError]               = useState('');

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      if (!u) navigate('/signin');
    });
    return () => unsub();
  }, [navigate]);

  // Ensure reCAPTCHA container
  const ensureHostDiv = () => {
    let host = document.getElementById('recaptcha-container');
    if (!host) {
      host = document.createElement('div');
      host.id = 'recaptcha-container';
      document.body.appendChild(host);
    }
    return host;
  };

  // Setup reCAPTCHA
  const getRecaptcha = async () => {
    ensureHostDiv();
    if (window._recaptcha) return window._recaptcha;
    window._recaptcha = new RecaptchaVerifier(
      'recaptcha-container',
      { size: 'invisible' },
      auth
    );
    await window._recaptcha.render();
    return window._recaptcha;
  };

  const handleSendCode = async () => {
    setError('');
    setLoading(true);
    try {
      // phoneValue is already E.164 if the user chose a country; 
      // if they typed something incomplete, it might be invalid
      if (!phoneValue || phoneValue.length < 5) {
        throw new Error('Enter a valid phone number.');
      }

      const verifier = await getRecaptcha();
      const result   = await signInWithPhoneNumber(auth, phoneValue, verifier);

      setConfirmationResult(result);
      alert('SMS code sent! Check your phone.');
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);
    try {
      if (!confirmationResult) throw new Error('Click “Send Code” first.');
      if (!verificationCode)   throw new Error('Enter the SMS code.');

      // Credential
      const credential = PhoneAuthProvider.credential(
        confirmationResult.verificationId,
        verificationCode
      );

      // Link to existing user or sign in
      let targetUser = auth.currentUser;
      if (targetUser?.email) {
        await linkWithCredential(targetUser, credential);
      } else {
        targetUser = (await signInWithCredential(auth, credential)).user;
      }

      // Write phone to Firestore
      await updateDoc(userDoc(targetUser.uid), {
        phoneNumber: phoneValue,
        phoneVerified: true,
      });

      alert('Phone verified & saved!');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (window._recaptcha) {
        window._recaptcha.clear();
        delete window._recaptcha;
      }
    };
  }, []);

  return (
    <div style={{ padding: '20px', minHeight: '60vh' }}>
      <div
        style={{
          maxWidth: '600px',
          margin: '0 auto',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#333',
          padding: '20px',
          borderRadius: '8px',
        }}
      >
        <h2
          style={{
            marginTop: 0,
            marginBottom: '1rem',
            textAlign: 'center',
            color: '#853a38',
          }}
        >
          Add or Verify Phone
        </h2>

        {error && <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>}

        {/* RECAPTCHA container or invisible */}
        <div id="recaptcha-container" />

        {/* Instead of dial code + local phone, we use a single <PhoneInput> */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Phone Number:
          </label>
          <PhoneInput
            defaultCountry="US"
            placeholder="Enter phone number"
            value={phoneValue}
            onChange={setPhoneValue}
            style={{
              width: '100%',
              borderRadius: '4px',
            }}
          />
        </div>

        {!confirmationResult && (
          <button onClick={handleSendCode} disabled={loading} style={btnStyle('#853a38')}>
            {loading ? 'Sending…' : 'Send Verification Code'}
          </button>
        )}

        {confirmationResult && (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                Enter the SMS Code:
              </label>
              <input
                type="text"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                style={{ width: '100%', padding: '8px', borderRadius: '4px' }}
              />
            </div>

            <button onClick={handleVerifyCode} disabled={loading} style={btnStyle('#006400')}>
              {loading ? 'Verifying…' : 'Verify Code'}
            </button>
          </>
        )}

        <button
          onClick={() => navigate('/dashboard')}
          style={btnStyle('#444')}
        >
          Cancel / Back to Dashboard
        </button>
      </div>
    </div>
  );
}

const btnStyle = (bg) => ({
  width: '100%',
  padding: '12px',
  backgroundColor: bg,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginBottom: '1rem',
});

export default AddPhone;
